import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, TrendingUp, Target, Package, Shield, Lock, Rocket, Users, Calendar, FileText, DollarSign, Building2, Award, FileCheck, BarChart3, Banknote, ExternalLink, FileDown } from 'lucide-react';
import { cn } from '../lib/utils';
import { useLanguage } from '../contexts/LanguageContext';

const iconMap = { Home, TrendingUp, Target, Package, Shield, Lock, Rocket, Users, Calendar, FileText, DollarSign, Building2, Award, FileCheck, BarChart3, Banknote };

const menuSections = {
  fr: [
    {
      partie: "PARTIE I – STRATÉGIE DE MARCHÉ",
      color: '#8b5cf6',
      items: [
        { path: '/', label: 'Synthèse Globale', icon: 'Home' },
        { path: '/marche', label: 'Marché — Écoles', icon: 'TrendingUp' },
        { path: '/marche-garderies', label: 'Marché — Garderies', icon: 'TrendingUp' },
        { path: '/vision', label: 'Vision & Objectifs', icon: 'Target' }
      ]
    },
    {
      partie: "PARTIE II – INFRASTRUCTURE TECHNOLOGIQUE",
      color: '#06b6d4',
      items: [
        { path: '/produit', label: 'Produit & Expérience', icon: 'Package' },
        { path: '/propriete-intellectuelle', label: 'Propriété Intellectuelle', icon: 'Shield' },
        { path: '/paiements', label: 'Paiements & Sécurité', icon: 'Lock' }
      ]
    },
    {
      partie: "PARTIE III – PLAN D'EXÉCUTION",
      color: '#fb923c',
      items: [
        { path: '/plan-execution', label: "Plan d'Exécution Opérationnel", icon: 'Rocket' },
        { path: '/gtm', label: 'Go-to-Market', icon: 'Users' },
        { path: '/roadmap', label: 'Roadmap & Jalons', icon: 'Calendar' }
      ]
    },
    {
      partie: "PARTIE IV – MODÈLE FINANCIER",
      color: '#a8d530',
      items: [
        { path: '/resume-executif', label: 'Résumé Exécutif', icon: 'FileText' },
        { path: '/modele', label: "Modèle d'Affaires", icon: 'DollarSign' },
        { path: '/organisation', label: 'Organisation & Coûts', icon: 'Building2' },
        { path: '/valorisation', label: 'Valorisation', icon: 'Award' },
        { path: '/levee-de-fonds', label: 'Levée de Fonds', icon: 'Banknote' },
        { path: '/synthese-investisseur', label: 'Synthèse Investisseur', icon: 'FileCheck' },
        { path: '/previsions', label: 'Prévisions Financières', icon: 'BarChart3' }
      ]
    }
  ],
  en: [
    {
      partie: "PART I – MARKET STRATEGY",
      color: '#8b5cf6',
      items: [
        { path: '/', label: 'Global Overview', icon: 'Home' },
        { path: '/marche', label: 'Market — Schools', icon: 'TrendingUp' },
        { path: '/marche-garderies', label: 'Market — Daycares', icon: 'TrendingUp' },
        { path: '/vision', label: 'Vision & Objectives', icon: 'Target' }
      ]
    },
    {
      partie: "PART II – TECHNOLOGICAL INFRASTRUCTURE",
      color: '#06b6d4',
      items: [
        { path: '/produit', label: 'Product & Experience', icon: 'Package' },
        { path: '/propriete-intellectuelle', label: 'Intellectual Property', icon: 'Shield' },
        { path: '/paiements', label: 'Payments & Security', icon: 'Lock' }
      ]
    },
    {
      partie: "PART III – EXECUTION PLAN",
      color: '#fb923c',
      items: [
        { path: '/plan-execution', label: 'Operational Execution Plan', icon: 'Rocket' },
        { path: '/gtm', label: 'Go-to-Market', icon: 'Users' },
        { path: '/roadmap', label: 'Roadmap & Milestones', icon: 'Calendar' }
      ]
    },
    {
      partie: "PART IV – FINANCIAL MODEL",
      color: '#a8d530',
      items: [
        { path: '/resume-executif', label: 'Executive Summary', icon: 'FileText' },
        { path: '/modele', label: 'Business Model', icon: 'DollarSign' },
        { path: '/organisation', label: 'Organization & Costs', icon: 'Building2' },
        { path: '/valorisation', label: 'Valuation', icon: 'Award' },
        { path: '/levee-de-fonds', label: 'Fundraising', icon: 'Banknote' },
        { path: '/synthese-investisseur', label: 'Investor Summary', icon: 'FileCheck' },
        { path: '/previsions', label: 'Financial Forecast', icon: 'BarChart3' }
      ]
    }
  ]
};

export default function Sidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleToggleMenu = () => {
      setIsOpen(prev => !prev);
    };
    
    window.addEventListener('toggleSidebar', handleToggleMenu);
    return () => window.removeEventListener('toggleSidebar', handleToggleMenu);
  }, []);

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed top-0 left-0 h-screen bg-black/95 backdrop-blur-sm border-r border-lime-500/30 transition-all duration-300 z-40 overflow-y-auto",
          isOpen ? "w-full sm:w-80 lg:w-[20%] lg:min-w-[280px]" : "w-0"
        )}
      >
        {isOpen && (
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_foodtech-finplan/artifacts/5cp42cda_image.png" 
                alt="EcoLunch" 
                className="h-10 w-auto"
              />
            </div>

            {/* Menu groupé par sections */}
            <nav className="space-y-6">
              {menuSections[language].map((section, sIdx) => {
                // Calculer le numéro de départ pour cette section
                let startNum = 1;
                for (let i = 0; i < sIdx; i++) {
                  startNum += menuSections[language][i].items.length;
                }
                
                return (
                  <div key={sIdx}>
                    {/* Section header */}
                    <div className="flex items-center gap-2 mb-3 px-2">
                      <span 
                        className="w-3 h-3 rounded-sm flex-shrink-0"
                        style={{ backgroundColor: section.color }}
                      ></span>
                      <span 
                        className="text-[10px] font-bold uppercase tracking-wider"
                        style={{ color: section.color }}
                      >
                        {section.partie}
                      </span>
                    </div>

                    {/* Section items */}
                    <div className="space-y-1">
                      {section.items.map((item, iIdx) => {
                        const isActive = location.pathname === item.path;
                        const pageNum = startNum + iIdx;
                        const Icon = iconMap[item.icon];
                        
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => {
                              if (isActive) {
                                setIsOpen(false);
                              }
                            }}
                            className={cn(
                              "flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm",
                              isActive 
                                ? "bg-lime-500/20 text-lime-400 border border-lime-500/50 shadow-md shadow-lime-500/20" 
                                : "text-gray-300 hover:text-lime-400 hover:bg-lime-500/10"
                            )}
                          >
                            <span className="text-xs text-gray-500 w-6">{pageNum}.</span>
                            {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
                            <span>{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </nav>

            {/* External Links Section - Mobile */}
            <div className="mt-8 pt-6 border-t border-lime-500/20">
              <div className="flex items-center gap-2 mb-3 px-2">
                <span className="w-3 h-3 rounded-sm flex-shrink-0 bg-gradient-to-r from-lime-400 to-cyan-400"></span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  {language === 'fr' ? 'LIENS RAPIDES' : 'QUICK LINKS'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => { window.open('/presentation-ecolunch', '_blank'); setIsOpen(false); }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-lime-400 to-green-500 text-black shadow-[0_0_10px_rgba(132,204,22,0.4)]"
                >
                  <ExternalLink className="h-3 w-3" />
                  Présentation
                </button>
                <button
                  onClick={() => { window.open('/ecosysteme-portails', '_blank'); setIsOpen(false); }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-blue-400 to-blue-600 text-white shadow-[0_0_10px_rgba(59,130,246,0.4)]"
                >
                  <ExternalLink className="h-3 w-3" />
                  Écosystème
                </button>
                <button
                  onClick={() => { window.open('/organigramme', '_blank'); setIsOpen(false); }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-cyan-400 to-teal-500 text-black shadow-[0_0_10px_rgba(34,211,238,0.4)]"
                >
                  <ExternalLink className="h-3 w-3" />
                  Organigramme
                </button>
                <button
                  disabled
                  title="Bientôt disponible"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-gray-600 to-gray-700 text-gray-400 opacity-50 cursor-not-allowed"
                >
                  <ExternalLink className="h-3 w-3" />
                  Invest Deck
                </button>
                <button
                  disabled
                  title="Bientôt disponible"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-gray-600 to-gray-700 text-gray-400 opacity-50 cursor-not-allowed"
                >
                  <ExternalLink className="h-3 w-3" />
                  Ronde Seed
                </button>
                <button
                  onClick={() => { window.open('/print-all', '_blank'); setIsOpen(false); }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white shadow-[0_0_10px_rgba(236,72,153,0.4)]"
                >
                  <FileDown className="h-3 w-3" />
                  PDF
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-12 pt-6 border-t border-lime-500/20">
              <p className="text-xs text-gray-500 text-center">
                EcoLunch © 2026
                <br />
                <span className="text-lime-500">{language === 'fr' ? 'Plan d\'Affaires' : 'Business Plan'}</span>
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
